export enum TypeAUncertaintyEvaluations {
  Experimental = 0,
  PooledEstimate = 1
}

export enum ObservationMethod {
  Direct = 0,
  Indirect = 1
}

export enum TypeAUncertaintyEstimates {
  Standard = 0,
  Bayesian = 1
}

export type DistributionType =
  | 'Automatic'
  | 'Normal'
  | 'Rectangular'
  | 'Trapezoidal'
  | 'Triangular'
  | 'StudentT';

export type FiniteNumberOrPositiveInfinity = number | 'Infinity';
export type SupportedStandardUncertainty = number | 'Infinity';

export interface ValueContainer {
  Value: number;
}

export interface DistributionBaseDTO {
  $type: DistributionType;
  CoverageFactor: number;
  ShapeFactor: number;
}

export interface MeasurementModel {
  UncertaintyEvaluation: TypeAUncertaintyEvaluations;
  StandardDeviation: number;
  Observations: number[];
  IndirectObservations: number[];
  ObservationMethod: ObservationMethod;
  UncertaintyEstimate: TypeAUncertaintyEstimates;
  DoF: FiniteNumberOrPositiveInfinity;
  Value: number;
  StandardUncertainty: number;
  SensitivityCoeff: number;
  UncertaintyContribution: number;
  RelativeContribution: number;
}

export interface UncertaintyBaseDTO<TStandardUncertainty = number> {
  Distribution: DistributionBaseDTO;
  DoF: FiniteNumberOrPositiveInfinity;
  Value: number;
  StandardUncertainty: TStandardUncertainty;
  SensitivityCoeff: number;
  UncertaintyContribution: number;
  RelativeContribution: number;
}

export interface LimitUncertaintyDTO<TStandardUncertainty = number>
  extends UncertaintyBaseDTO<TStandardUncertainty> {
  HalfWidthOfLimit: number;
  RelativeErrorOfUncertainty: number;
}

export interface ExpandedUncertaintyDTO extends UncertaintyBaseDTO<number> {
  ExpandedUncertainty: number;
  CoverageFactor: number;
}

export interface NModel {
  Value: string;
  Tolerance: number;
  TUR?: number;
  LimitValue?: number;
  Limit: number;
  LowerLimit: number;
  UpperLimit: number;
  DecisionRule: number;
  Distribution: DistributionBaseDTO;
}

export interface DataEntityVolumeFormModel {
  Unit?: string;
  UncertaintyFloat?: number;
  m0: ValueContainer;
  k0: ValueContainer;
  pb: ValueContainer;
  a0: ValueContainer;
  a1: ValueContainer;
  a2: ValueContainer;
  a3: ValueContainer;
  a4: ValueContainer;
  aC: ValueContainer;
  td20: ValueContainer;
  m2: MeasurementModel[];
  mE: LimitUncertaintyDTO;
  DmuB: ExpandedUncertaintyDTO;
  DmLB: LimitUncertaintyDTO;
  DmRB1: LimitUncertaintyDTO;
  DmRB2: LimitUncertaintyDTO;
  DDmrB1: LimitUncertaintyDTO;
  DDmrB2: LimitUncertaintyDTO<SupportedStandardUncertainty>;
  DmTDB: LimitUncertaintyDTO;
  Pa_IND: LimitUncertaintyDTO;
  Qa_IND: LimitUncertaintyDTO;
  ta_IND: LimitUncertaintyDTO;
  tW: LimitUncertaintyDTO;
  td: LimitUncertaintyDTO;
  Description: string;
  Expert: string;
  IssueDateMiladi: string;
  CalibrationDateMiladi: string;
  RecalibrationDateMiladi: string;
  Manufacturer: string;
  ModelDevice: string;
  TypeDevice: string;
  CodeNoDevice: string;
  SerialNoDevice: string;
  CustomerName: string;
  CertificateAddress: string;
  LabAdderss: string;
  Farsi: boolean;
  Value: number;
  From: number;
  To: number;
  Division: number;
  LetterId: number;
  CalibrationDetailId: number;
  NModels: NModel[];
}

export type DataEntityVolumeSaveRequest = DataEntityVolumeFormModel;

export interface DataEntityVolumeCertificate {
  name: string | null;
  description: string | null;
  formulas: CertificateFormula[];
  regression: CertificateRegression | null;
}

export interface CertificateFormula {
  id: number | null;
  name: string | null;
  value: string | null;
  subValues: string[];
  result: CertificateFormulaResult | null;
  parameters: CertificateParameter[];
  constants: CertificateConstant[];
  regression: CertificateRegression | null;
  monteCarloSimulation: CertificateMonteCarloSimulation | null;
}

export interface CertificateFormulaResult {
  distribution: CertificateDistribution | null;
  capabilityProof: CertificateCapabilityProof | null;
  complianceAssessment: CertificateComplianceAssessment | null;
  doF: number | 'Infinity' | null;
  value: number | 'Infinity' | null;
  standardUncertainty: number | 'Infinity' | null;
  expandedUncertainty: number | 'Infinity' | null;
  resultDistribution: CertificateResultDistribution | null;
  name: string | null;
  unit: string | null;
}

export interface CertificateDistribution {
  $type?: string | null;
  coverageFactor: number | null;
  shapeFactor: number | null;
}

export interface CertificateResultDistribution {
  coverageFactor: number | null;
  shapeFactor: number | null;
}

export interface CertificateCapabilityProof {
  tolerance: number | null;
  limit: number | null;
  index: number | null;
  acceptance: boolean | null;
}

export interface CertificateComplianceAssessment {
  decisionRule: number | null;
  enableLowerLimit: boolean | null;
  lowerLimit: number | null;
  enableUpperLimit: boolean | null;
  upperLimit: number | null;
  acceptanceLowerLimit: number | null;
  acceptanceUpperLimit: number | null;
  rejectionLowerLimit: number | null;
  rejectionUpperLimit: number | null;
  value: number | null;
  valueLowerLimit: number | null;
  valueUpperLimit: number | null;
  insideProbability: number | null;
  outsideProbability: number | null;
  acceptance: boolean | null;
}

export interface CertificateParameter {
  doF: number | 'Infinity' | null;
  value: number | 'Infinity' | null;
  standardUncertainty: number | 'Infinity' | null;
  sensitivityCoeff: number | 'Infinity' | null;
  uncertaintyContribution: number | 'Infinity' | null;
  relativeContribution: number | 'Infinity' | null;
  distribution: CertificateDistribution | null;
  name: string | null;
  unit: string | null;
}

export interface CertificateConstant {
  value: number | 'Infinity' | null;
  name: string | null;
  unit: string | null;
}

export interface CertificateRegression {
  quantities: string[];
  expandedUncertainies: string[];
  slope: number | null;
  intercept: number | null;
  equation: string | null;
  correlation: number | null;
  uncertaintyEquation: string | null;
  bPrime: number | null;
}

export interface CertificateMonteCarloSimulation {
  enabled: boolean | null;
  numSamples: number | null;
  value: number | null;
  standardDeviation: number | null;
  standardUncertainy: number | null;
  expandedUncertainy: number | null;
  coverageMin: number | null;
  coverageMax: number | null;
  coverageFactor: number | null;
  capabilityLimit: number | null;
  capabilityIndex: number | null;
  capabilityAccept: boolean | null;
  compliancePInside: number | null;
  compliancePOutside: number | null;
  complianceAccept: boolean | null;
  minimum: number | null;
  maximum: number | null;
  median: number | null;
  mean: number | null;
  values: number[];
  frequencies: number[];
}

export type DataEntityVolumeCertificateFormula = CertificateFormula;
export type DataEntityVolumeCertificateFormulaResult = CertificateFormulaResult;
export type DataEntityVolumeCertificateCapabilityProof = CertificateCapabilityProof;
export type DataEntityVolumeCertificateComplianceAssessment = CertificateComplianceAssessment;
export type DataEntityVolumeCertificateDistribution = CertificateDistribution;
export type DataEntityVolumeCertificateParameters = CertificateParameter[];
export type DataEntityVolumeCertificateConstants = CertificateConstant[];
export type DataEntityVolumeCertificateRegression = CertificateRegression;
export type DataEntityVolumeCertificateMonteCarloSimulation = CertificateMonteCarloSimulation;

export interface DataEntityVolumeApiResponse {
  samplerModel: DataEntityVolumeLookupSamplerModel | null;
  certificate: DataEntityVolumeCertificate | null;
}

export type DataEntityVolumeLookupResponse = DataEntityVolumeApiResponse;
export type DataEntityVolumeSaveResponse = DataEntityVolumeApiResponse;

export interface DataEntityVolumeLookupSamplerModel {
  unit?: string | null;
  uncertaintyFloat?: number | null;
  m0: ValueContainer | null;
  k0: ValueContainer | null;
  pb: ValueContainer | null;
  a0: ValueContainer | null;
  a1: ValueContainer | null;
  a2: ValueContainer | null;
  a3: ValueContainer | null;
  a4: ValueContainer | null;
  aC: ValueContainer | null;
  td20: ValueContainer | null;

  m2: MeasurementModel[];

  mE: LimitUncertaintyDTO | null;
  DmuB: ExpandedUncertaintyDTO | null;
  DmLB: LimitUncertaintyDTO | null;
  DmRB1: LimitUncertaintyDTO | null;
  DmRB2: LimitUncertaintyDTO | null;
  DDmrB1: LimitUncertaintyDTO | null;
  DDmrB2: LimitUncertaintyDTO<SupportedStandardUncertainty> | null;
  DmTDB: LimitUncertaintyDTO | null;
  Pa_IND: LimitUncertaintyDTO | null;
  Qa_IND: LimitUncertaintyDTO | null;
  ta_IND: LimitUncertaintyDTO | null;
  tW: LimitUncertaintyDTO | null;
  td: LimitUncertaintyDTO | null;

  description: string | null;
  expert: string | null;
  certificatefile: string | null;
  uncertaintyReportFile?: string | null;

  issueDateMiladi: string | null;
  calibrationDateMiladi: string | null;
  recalibrationDateMiladi: string | null;

  manufacturer: string | null;
  modelDevice: string | null;
  typeDevice: string | null;
  serialNoDevice: string | null;
  codeNoDevice: string | null;

  labAdderss: string | null;
  farsi: boolean | null;

  value: number | null;
  from: number | null;
  to: number | null;
  division: number | null;

  customerName: string | null;
  certificateAddress: string | null;

  letterId: number | null;
  calibrationDetailId: number | null;

  behfaDeviceCodes?: unknown[];
  nModels: NModel[];
}

export type DataEntityVolumeLookupModel = Readonly<Record<string, unknown>>;

export interface AspNetValidationProblemDetails {
  title?: string;
  detail?: string;
  errors?: Record<string, string[] | string>;
}
